import React, { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { WebsiteTileViewModel } from '@rocco/ui/tile-website/view-models/website';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';
import { getAspectRatioClass } from '@rocco/ui/tile-shared/utils/aspect-ratio';

/**
 * WebsiteTile displays an external link with a thumbnail.
 * When there's a real scraped image, it shows a blurred background effect with
 * the thumbnail at 80% width. When using a placeholder, it shows a single
 * image with object-cover to fill the container.
 */
export const WebsiteTile = (props: TileViewModel<WebsiteTileViewModel>) => {
    const {
        aspectRatio,
        model,
        inlineActionButtons,
        onClick,
        disableLink = false,
        className,
        contentEditable,
    } = props;

    const actionsContext = useAtomValue(uiContextAtomTileActions);
    const { onStaticTileClick } = actionsContext?.trackingConfig ?? {};
    const tileRef = useRef<HTMLAnchorElement>(null);

    const handleTileClick = (
        e:
            | React.MouseEvent<HTMLDivElement>
            | React.MouseEvent<HTMLAnchorElement>
    ) => {
        if (onStaticTileClick || onClick) {
            e.preventDefault();
            e.stopPropagation();
            onClick?.();
            onStaticTileClick?.(model.title);
        }
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            to={model.link ?? ''}
            onClick={handleTileClick}
            className={'block'}
        >
            <div
                className={cn(
                    'group flex h-full flex-col gap-[1.125rem] md:gap-[1.2rem]',
                    className
                )}
            >
                <div className="group relative h-full">
                    <div className="flex flex-col gap-4">
                        {/* Media container */}
                        <div
                            className={cn(
                                'rocco-tile-media',
                                getAspectRatioClass(aspectRatio),
                                'relative flex items-center justify-center overflow-hidden rounded-tile bg-gray-300'
                            )}
                        >
                            {model.hasImage ? (
                                <>
                                    {/* Blurred background image for scraped images */}
                                    <img
                                        src={model.thumbnail.src}
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute z-0 h-full w-auto blur-[3px] brightness-50"
                                    />
                                    {/* Foreground thumbnail with rounded corners */}
                                    <img
                                        src={model.thumbnail.src}
                                        alt={model.thumbnail.alt}
                                        className="relative z-[1] w-4/5 rounded"
                                    />
                                </>
                            ) : (
                                /* Placeholder image with object-cover */
                                <img
                                    src={model.thumbnail.src}
                                    alt={model.thumbnail.alt}
                                    className="h-full w-full object-cover"
                                />
                            )}
                        </div>
                        <div className="flex flex-col gap-4">
                            {inlineActionButtons && (
                                <div className="flex flex-col">
                                    {inlineActionButtons}
                                </div>
                            )}
                            <div
                                className={`${TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK} flex flex-col gap-[0.125rem]`}
                            >
                                <span
                                    contentEditable={
                                        contentEditable?.enabled.title
                                    }
                                    role={
                                        contentEditable?.enabled.title
                                            ? 'textbox'
                                            : undefined
                                    }
                                    aria-label={
                                        contentEditable?.enabled.title
                                            ? 'Edit title'
                                            : undefined
                                    }
                                    aria-multiline="false"
                                    suppressContentEditableWarning={true}
                                    onInput={(
                                        e: React.FormEvent<HTMLSpanElement>
                                    ) =>
                                        contentEditable?.onChange?.(
                                            (e.target as HTMLSpanElement)
                                                .textContent ?? '',
                                            'title'
                                        )
                                    }
                                    onBlur={() => {
                                        contentEditable?.onBlur?.();
                                    }}
                                    onKeyDown={(
                                        e: React.KeyboardEvent<HTMLSpanElement>
                                    ) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            (
                                                e.target as HTMLSpanElement
                                            ).blur();
                                        }
                                    }}
                                    className={cn(
                                        'overflow-hidden text-ellipsis text-sm font-medium tracking-[0.03rem] md:text-base md:tracking-[0.02rem]',
                                        contentEditable?.enabled.title
                                            ? 'truncate'
                                            : 'line-clamp-1',
                                        contentEditable?.enabled.title &&
                                            'focus-within:border-primary-500 -mx-2 cursor-text whitespace-nowrap rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500'
                                    )}
                                >
                                    {model.title}
                                </span>
                                {model.description && (
                                    <span
                                        contentEditable={
                                            contentEditable?.enabled.description
                                        }
                                        role={
                                            contentEditable?.enabled.description
                                                ? 'textbox'
                                                : undefined
                                        }
                                        aria-label={
                                            contentEditable?.enabled.description
                                                ? 'Edit description'
                                                : undefined
                                        }
                                        aria-multiline="false"
                                        suppressContentEditableWarning={true}
                                        onInput={(
                                            e: React.FormEvent<HTMLSpanElement>
                                        ) =>
                                            contentEditable?.onChange?.(
                                                (e.target as HTMLSpanElement)
                                                    .textContent ?? '',
                                                'description'
                                            )
                                        }
                                        onBlur={() => {
                                            contentEditable?.onBlur?.();
                                        }}
                                        onKeyDown={(
                                            e: React.KeyboardEvent<HTMLSpanElement>
                                        ) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                (
                                                    e.target as HTMLSpanElement
                                                ).blur();
                                            }
                                        }}
                                        className={cn(
                                            'overflow-hidden text-ellipsis text-sm font-medium tracking-[0.03rem] text-gray-900/40 md:text-base md:tracking-[0.02rem]',
                                            !contentEditable?.enabled
                                                .description && 'text-nowrap',
                                            contentEditable?.enabled
                                                .description &&
                                                'focus-within:border-primary-500 -ml-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500'
                                        )}
                                    >
                                        {model.description}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </OptionalLink>
    );
};
